import React, { useState, useEffect } from 'react';
import { Box, Stack, Typography, CircularProgress, LinearProgress, Fade } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import DashboardLayout from '../Layouts/DashboardLayout';
import HeatmapResultsHeader from './HeatmapResultsHeader';
import HeatmapStatsSection from './HeatmapStatsSection';
import HeatmapMapSection from './HeatmapMapSection';
import HeatmapHistorySection from './HeatmapHistorySection';
import { RankingService } from '../../heatmap/lib/ranking-service';
import { saveHeatmapSnapshot } from '../../utils/yourDataStore';
import './HeatmapResponsiveFixes.css';

import logger from '../../utils/logger';
import { usePermissions } from '../../contexts/PermissionContext';
import CompetitorAnalysisService from '../../services/CompetitorAnalysisService';
import {
  TrendingUp,
  LocationOn,
  Search,
  Assessment
} from '@mui/icons-material';

const HeatmapResultsPage = () => {
  const [searchParams] = useSearchParams();
  const [resultsData, setResultsData] = useState(null);
  const [error, setError] = useState(null);
  const [currentKeyword, setCurrentKeyword] = useState('');

  const { isAdmin, canAccessFeature, hasPlanAccess, loading: permsLoading, triggerUpgradeNotice } = usePermissions();

  // Extract parameters from URL
  const businessName = searchParams.get('businessName') || '';
  const urlKeyword = searchParams.get('keyword') || '';
  const keyword = currentKeyword || urlKeyword;
  const gridSize = parseInt(searchParams.get('gridSize')) || 5;
  const radius = parseFloat(searchParams.get('radius')) || 2;
  const locationId = searchParams.get('locationId') || null;

  // Admin view mode parameters - load stored analysis instead of running new one
  const analysisId = searchParams.get('analysisId') || null;
  const adminViewUserId = searchParams.get('userId') || null;
  const isAdminView = searchParams.get('adminView') === 'true';

  // Handle coordinates with fallbacks
  const latParam = searchParams.get('lat');
  const lngParam = searchParams.get('lng');
  const lat = latParam ? parseFloat(latParam) : null;
  const lng = lngParam ? parseFloat(lngParam) : null;

  // Default coordinates for Zirakpur/Mohali area if none provided
  const defaultLat = 30.6463145;
  const defaultLng = 76.8220304;

  logger.log('URL Parameters:', { latParam, lngParam, lat, lng, analysisId, adminViewUserId, isAdminView });

  // Initialize current keyword from URL
  React.useEffect(() => {
    if (urlKeyword && !currentKeyword) {
      setCurrentKeyword(urlKeyword);
    }
  }, [urlKeyword, currentKeyword]);

  useEffect(() => {
    let active = true;

    // Admin view mode: Load stored analysis from database instead of running new analysis
    async function loadStoredAnalysis() {
      if (!analysisId || !adminViewUserId) {
        logger.warn('[HeatmapResults] Admin view requested but missing analysisId or userId');
        return false;
      }

      try {
        logger.log('[HeatmapResults] 🔍 Admin view mode - loading stored analysis:', { analysisId, adminViewUserId });

        // Fetch the stored analysis by ID
        const response = await CompetitorAnalysisService.getById(analysisId);

        if (!response.success || !response.data) {
          logger.warn('[HeatmapResults] Could not find analysis with ID:', analysisId);
          return false;
        }

        const analysisData = response.data;

        logger.log('[HeatmapResults] ✅ Found stored analysis:', {
          id: analysisData.id,
          business_name: analysisData.business_name,
          keyword: analysisData.keyword,
          analysis_date: analysisData.analysis_date
        });

        // Parse competitors_data which contains the grid/heatmap information
        let competitorsData = null;
        if (analysisData.competitors_data) {
          competitorsData = typeof analysisData.competitors_data === 'string'
            ? JSON.parse(analysisData.competitors_data)
            : analysisData.competitors_data;
        }

        // Handle both old format (array) and new format (object with gridData)
        // Old format: competitors_data is an array of competitors
        // New format: competitors_data is { competitors: [...], gridData: {...}, ... }
        let gridData = null;
        if (competitorsData) {
          if (Array.isArray(competitorsData)) {
            // Old format - no gridData available in competitors_data
            logger.log('[HeatmapResults] Old format detected (array), will try HeatmapData table');
          } else if (competitorsData.gridData?.results) {
            // New format - gridData is embedded
            gridData = competitorsData.gridData;
            logger.log('[HeatmapResults] New format detected with embedded gridData');
          }
        }

        // If no gridData in competitors_data, try fetching from HeatmapData table
        if (!gridData) {
          try {
            logger.log('[HeatmapResults] Fetching grid data from HeatmapData table...');
            const heatmapResponse = await fetch(
              `/api/heatmap-data/latest?userId=${adminViewUserId}&locationId=${analysisData.location_id || ''}&businessName=${encodeURIComponent(analysisData.business_name)}&keyword=${encodeURIComponent(analysisData.keyword)}`
            );
            if (heatmapResponse.ok) {
              const heatmapResult = await heatmapResponse.json();
              if (heatmapResult.success && heatmapResult.data?.grid_results) {
                let gridResults = heatmapResult.data.grid_results;
                if (typeof gridResults === 'string') {
                  gridResults = JSON.parse(gridResults);
                }
                gridData = {
                  results: gridResults,
                  gridSize: heatmapResult.data.grid_size || 5,
                  radius: heatmapResult.data.radius || 2,
                  centerLocation: {
                    lat: parseFloat(heatmapResult.data.center_lat),
                    lng: parseFloat(heatmapResult.data.center_lng)
                  }
                };
                logger.log('[HeatmapResults] Successfully loaded gridData from HeatmapData table');
              }
            }
          } catch (heatmapErr) {
            logger.warn('[HeatmapResults] Could not fetch from HeatmapData table:', heatmapErr.message);
          }
        }

        // Check if we have grid data from either source
        if (!gridData?.results) {
          logger.warn('[HeatmapResults] No grid data found in stored analysis or HeatmapData table');
          return false;
        }
        const storedGridSize = gridData.gridSize || 5;
        const storedRadius = gridData.radius || 2;

        // Convert stored results to UI shape (pins)
        const pins = gridData.results.map((r, idx) => ({
          id: idx,
          rank: r.targetBusinessRank ?? '20+',
          color: (r.targetBusinessRank == null) ? '#EF232A' : (r.targetBusinessRank <= 3 ? '#34A853' : r.targetBusinessRank <= 7 ? '#FBBC05' : '#EF232A'),
          position: { row: r.gridPoint?.gridY || Math.floor(idx / storedGridSize), col: r.gridPoint?.gridX || (idx % storedGridSize) },
        }));

        // Get businesses list from center result
        const midIndex = Math.floor((storedGridSize * storedGridSize) / 2);
        const centerResult = gridData.results[midIndex] || gridData.results[0];
        const businesses = (centerResult?.businesses || []).slice(0, 10).map((b) => ({
          name: b.name,
          rank: b.originalRank || null,
          address: b.address || b.vicinity || '',
          rating: b.rating || null,
          reviews: b.userRatingsTotal || b.reviews || null,
          isYou: (b.name || '').toLowerCase().includes((analysisData.business_name || '').toLowerCase()),
        }));

        // Calculate stats from pins
        const total = pins.length;
        const goodCount = pins.filter(p => p.rank !== '20+' && typeof p.rank === 'number' && p.rank <= 3).length;
        const avgCount = pins.filter(p => p.rank !== '20+' && typeof p.rank === 'number' && p.rank > 3 && p.rank <= 7).length;
        const poorCount = pins.filter(p => p.rank === '20+' || (typeof p.rank === 'number' && p.rank > 7)).length;
        const outOfTop20Count = pins.filter(p => p.rank === '20+').length;

        // Calculate average rank
        const numericRanks = pins.filter(p => typeof p.rank === 'number').map(p => p.rank);
        const calculatedAvgRank = numericRanks.length > 0
          ? numericRanks.reduce((acc, rank) => acc + rank, 0) / numericRanks.length
          : null;

        const resultsPayload = {
          businessName: analysisData.business_name,
          keyword: analysisData.keyword,
          date: new Date(analysisData.analysis_date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
          stats: {
            good: Math.round((goodCount / total) * 100),
            poor: Math.round((poorCount / total) * 100),
            average: Math.round((avgCount / total) * 100),
            averageRank: calculatedAvgRank ? Math.round(calculatedAvgRank * 10) / 10 : '—',
            shareOfVoice: Math.round((goodCount / total) * 100),
            outOfTop20: Math.round((outOfTop20Count / total) * 100),
          },
          mapData: {
            center: gridData.centerLocation || { lat: defaultLat, lng: defaultLng },
            pins,
            businesses,
            radius: storedRadius,
          },
          history: [
            {
              gridIcon: true,
              gridSize: `${storedGridSize}*${storedGridSize}`,
              keyword: analysisData.keyword,
              radius: `${storedRadius} km`,
              listing: analysisData.business_name,
              averageRank: calculatedAvgRank || null,
              date: new Date(analysisData.analysis_date).toLocaleDateString(),
            }
          ],
          isAdminView: true,
          analysisId: analysisData.id
        };

        if (active) {
          setResultsData(resultsPayload);
          setCurrentKeyword(analysisData.keyword);
          logger.log('[HeatmapResults] ✅ Successfully loaded stored heatmap for admin view');
        }

        return true;
      } catch (error) {
        logger.error('[HeatmapResults] Error loading stored analysis:', error);
        return false;
      }
    }

    async function fetchRanking() {
      // If in admin view mode with analysisId, try to load that specific stored analysis
      if (isAdminView && analysisId && adminViewUserId) {
        const loaded = await loadStoredAnalysis();
        if (loaded) {
          return; // Successfully loaded stored analysis, don't run new analysis
        }
        logger.warn('[HeatmapResults] Failed to load stored analysis by ID, will try by location');
      }

      // If in admin view mode, try to load latest analysis by location or business name
      if (isAdminView && adminViewUserId && (locationId || businessName)) {
        try {
          logger.log('[HeatmapResults] 🔍 Admin view mode - trying to load latest analysis:', { adminViewUserId, locationId, businessName });

          let response = null;

          // Try by location first
          if (locationId) {
            try {
              response = await CompetitorAnalysisService.getLatestByLocation(adminViewUserId, locationId);
            } catch (locError) {
              logger.warn('[HeatmapResults] Location lookup failed:', locError.message);
            }
          }

          // Fallback to business name
          if (!response?.success && businessName) {
            try {
              response = await CompetitorAnalysisService.getLatest(adminViewUserId, businessName);
            } catch (nameError) {
              logger.warn('[HeatmapResults] Business name lookup failed:', nameError.message);
            }
          }

          if (response?.success && response?.data) {
            const analysisData = response.data;
            logger.log('[HeatmapResults] ✅ Found latest analysis:', analysisData.id);

            // Parse and display the stored analysis
            let competitorsData = null;
            if (analysisData.competitors_data) {
              competitorsData = typeof analysisData.competitors_data === 'string'
                ? JSON.parse(analysisData.competitors_data)
                : analysisData.competitors_data;
            }

            if (competitorsData?.gridData?.results) {
              const gridData = competitorsData.gridData;
              const storedGridSize = gridData.gridSize || 5;
              const storedRadius = gridData.radius || 2;

              // Convert stored results to UI shape (pins)
              const pins = gridData.results.map((r, idx) => ({
                id: idx,
                rank: r.targetBusinessRank ?? '20+',
                color: (r.targetBusinessRank == null) ? '#EF232A' : (r.targetBusinessRank <= 3 ? '#34A853' : r.targetBusinessRank <= 7 ? '#FBBC05' : '#EF232A'),
                position: { row: r.gridPoint?.gridY || Math.floor(idx / storedGridSize), col: r.gridPoint?.gridX || (idx % storedGridSize) },
              }));

              // Get businesses list from center result
              const midIndex = Math.floor((storedGridSize * storedGridSize) / 2);
              const centerResult = gridData.results[midIndex] || gridData.results[0];
              const businesses = (centerResult?.businesses || []).slice(0, 10).map((b) => ({
                name: b.name,
                rank: b.originalRank || null,
                address: b.address || b.vicinity || '',
                rating: b.rating || null,
                reviews: b.userRatingsTotal || b.reviews || null,
                isYou: (b.name || '').toLowerCase().includes((analysisData.business_name || '').toLowerCase()),
              }));

              // Calculate stats from pins
              const total = pins.length;
              const goodCount = pins.filter(p => p.rank !== '20+' && typeof p.rank === 'number' && p.rank <= 3).length;
              const avgCount = pins.filter(p => p.rank !== '20+' && typeof p.rank === 'number' && p.rank > 3 && p.rank <= 7).length;
              const poorCount = pins.filter(p => p.rank === '20+' || (typeof p.rank === 'number' && p.rank > 7)).length;
              const outOfTop20Count = pins.filter(p => p.rank === '20+').length;

              const numericRanks = pins.filter(p => typeof p.rank === 'number').map(p => p.rank);
              const calculatedAvgRank = numericRanks.length > 0
                ? numericRanks.reduce((acc, rank) => acc + rank, 0) / numericRanks.length
                : null;

              const resultsPayload = {
                businessName: analysisData.business_name,
                keyword: analysisData.keyword,
                date: new Date(analysisData.analysis_date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
                stats: {
                  good: Math.round((goodCount / total) * 100),
                  poor: Math.round((poorCount / total) * 100),
                  average: Math.round((avgCount / total) * 100),
                  averageRank: calculatedAvgRank ? Math.round(calculatedAvgRank * 10) / 10 : '—',
                  shareOfVoice: Math.round((goodCount / total) * 100),
                  outOfTop20: Math.round((outOfTop20Count / total) * 100),
                },
                mapData: {
                  center: gridData.centerLocation || { lat: defaultLat, lng: defaultLng },
                  pins,
                  businesses,
                  radius: storedRadius,
                },
                history: [
                  {
                    gridIcon: true,
                    gridSize: `${storedGridSize}*${storedGridSize}`,
                    keyword: analysisData.keyword,
                    radius: `${storedRadius} km`,
                    listing: analysisData.business_name,
                    averageRank: calculatedAvgRank || null,
                    date: new Date(analysisData.analysis_date).toLocaleDateString(),
                  }
                ],
                isAdminView: true,
                analysisId: analysisData.id
              };

              if (active) {
                setResultsData(resultsPayload);
                setCurrentKeyword(analysisData.keyword);
                logger.log('[HeatmapResults] ✅ Successfully loaded latest heatmap for admin view');
                return;
              }
            }
          }
        } catch (error) {
          logger.error('[HeatmapResults] Error loading latest analysis for admin view:', error);
        }
      }

      // Permission check: if user cannot access heatmap feature, show notice and skip
      try {
        if (!isAdmin && !permsLoading && !canAccessFeature('heatmapAnalysis')) {
          triggerUpgradeNotice('Heatmap');
          // do not run the ranking for unauthorized profiles
          return;
        }
      } catch (e) {
        // ignore permission errors and proceed (fail-open fallback)
        logger.warn('[HeatmapResults] permission check failed, proceeding', e);
      }

      try {
        setError(null);
        // DataForSEO credentials (can be in format "login:password" or separate env vars)
        const apiKey = process.env.REACT_APP_GOOGLE_API_KEY ||
          `${process.env.REACT_APP_DATAFORSEO_LOGIN}:${process.env.REACT_APP_DATAFORSEO_PASSWORD}` || '';

        logger.log('API Key available:', !!apiKey);
        logger.log('Config:', { businessName, keyword, gridSize, radius, lat, lng });
        
        // Use provided coordinates or fallback to defaults
        const numLat = (lat !== null && !isNaN(lat)) ? lat : defaultLat;
        const numLng = (lng !== null && !isNaN(lng)) ? lng : defaultLng;
        
        logger.log('Final coordinates:', { 
          provided: { lat, lng }, 
          final: { numLat, numLng }, 
          usingDefaults: lat === null || lng === null 
        });
        
        if (!apiKey) {
          throw new Error('DataForSEO API credentials not found. Please set REACT_APP_DATAFORSEO_LOGIN and REACT_APP_DATAFORSEO_PASSWORD in your .env file.');
        }
        
        // Enable live API to use production Visibeen backend endpoint
        // Third parameter: useNewAPI=true, useLiveAPI=true
        const svc = new RankingService(apiKey, true, true);

        const config = {
          businessName,
          businessAddress: '',
          keyword,
          centerLocation: { lat: numLat, lng: numLng },
          searchRadius: radius, // in km from page selection
          gridSize,
          googleApiKey: apiKey,
          depth: 20, // Get 20 competitors per grid cell
        };

        const { isValid, errors } = svc.validateConfig(config);
        if (!isValid) {
          throw new Error('Invalid configuration: ' + errors.join(', '));
        }

        const data = await svc.performRankingAnalysis(config);
        
        logger.log('RankingService data:', data);
        logger.log('Results count:', data.results?.length);

        // Convert to UI shape
        const pins = data.results.map((r, idx) => ({
          id: idx,
          rank: r.targetBusinessRank ?? '20+',
          color: (r.targetBusinessRank == null) ? '#EF232A' : (r.targetBusinessRank <= 3 ? '#34A853' : r.targetBusinessRank <= 7 ? '#FBBC05' : '#EF232A'),
          position: { row: r.gridPoint.gridY || Math.floor(idx / gridSize), col: r.gridPoint.gridX || (idx % gridSize) },
        }));
        
        logger.log('Generated pins:', pins);

        // Aggregate a business list from the center result (closest to middle index)
        const midIndex = Math.floor((gridSize * gridSize) / 2);
        const centerResult = data.results[midIndex] || data.results[0];
        const businesses = (centerResult?.businesses || []).slice(0, 10).map((b) => ({
          name: b.name,
          rank: b.originalRank || null,
          address: b.address || b.vicinity || '',
          rating: b.rating || null,
          reviews: b.userRatingsTotal || b.reviews || null,
          isYou: (b.name || '').toLowerCase().includes((businessName || '').toLowerCase()),
        }));

        // Calculate stats
        const total = pins.length;
        const goodCount = pins.filter(p => p.rank !== '20+' && typeof p.rank === 'number' && p.rank <= 3).length;
        const avgCount = pins.filter(p => p.rank !== '20+' && typeof p.rank === 'number' && p.rank > 3 && p.rank <= 7).length;
        const poorCount = pins.filter(p => p.rank === '20+' || (typeof p.rank === 'number' && p.rank > 7)).length;
        const outOfTop20Count = pins.filter(p => p.rank === '20+').length;
        
        // Calculate average rank from pins if not provided by API
        let calculatedAvgRank = data.averageRank;
        if (!calculatedAvgRank) {
          const numericRanks = pins.filter(p => typeof p.rank === 'number').map(p => p.rank);
          if (numericRanks.length > 0) {
            const sum = numericRanks.reduce((acc, rank) => acc + rank, 0);
            calculatedAvgRank = sum / numericRanks.length;
          }
        }
        
        const resultsPayload = {
          businessName: data.businessName,
          keyword: data.keyword,
          date: new Date(data.timestamp).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
          stats: {
            good: Math.round((goodCount / total) * 100),
            poor: Math.round((poorCount / total) * 100),
            average: Math.round((avgCount / total) * 100),
            averageRank: calculatedAvgRank ? Math.round(calculatedAvgRank * 10) / 10 : '—',
            shareOfVoice: Math.round((goodCount / total) * 100),
            outOfTop20: Math.round((outOfTop20Count / total) * 100),
          },
          mapData: {
            center: data.centerLocation,
            pins,
            businesses,
            radius,
          },
          history: [
            {
              gridIcon: true,
              gridSize: `${gridSize}*${gridSize}`,
              keyword,
              radius: `${radius} km`,
              listing: data.businessName,
              averageRank: data.averageRank || null,
              date: new Date(data.timestamp).toLocaleDateString(),
            }
          ],
        };

        if (active) {
          setResultsData(resultsPayload);
          
          // Save heatmap results to storage for Performance page
          // Try to get locationId from URL params or match from localStorage profiles
          let profileId = locationId;
          if (!profileId) {
            try {
              const cachedProfiles = localStorage.getItem('availableGMBProfiles');
              const cachedOwner = localStorage.getItem('availableGMBProfilesOwner');
              // compute current owner (session email preferred, allow admin-view when impersonating)
              const session = (() => {
                try { return JSON.parse(localStorage.getItem('user')) || {}; } catch (_) { return {}; }
              })();
              let currentOwner = (session?.email || session?.user?.email || '').toString().toLowerCase() || 'anonymous';
              try {
                const adminAccessDataRaw = sessionStorage.getItem('adminAccessData');
                if (adminAccessDataRaw) {
                  const adminAccess = JSON.parse(adminAccessDataRaw);
                  if (adminAccess?.targetUserId) currentOwner = `admin-view:${adminAccess.targetUserId}`;
                }
              } catch (_) {}

              if (cachedProfiles && cachedOwner && String(cachedOwner) === String(currentOwner)) {
                const profiles = JSON.parse(cachedProfiles);
                const matchedProfile = profiles.find(p => 
                  p.name && businessName && 
                  p.name.toLowerCase().trim() === businessName.toLowerCase().trim()
                );
                if (matchedProfile) {
                  profileId = matchedProfile.id || matchedProfile.locationId;
                  logger.log('[HeatmapResults] Matched profile ID from localStorage:', profileId);
                }
              }
            } catch (e) {
              logger.warn('[HeatmapResults] Could not match profile from localStorage:', e);
            }
          }
          
          const heatmapSnapshot = {
            businessName: resultsPayload.businessName,
            keyword: resultsPayload.keyword,
            gridSize,
            radius,
            centerLocation: data.centerLocation,
            stats: resultsPayload.stats,
            pins: resultsPayload.mapData.pins,
            results: data.results, // IMPORTANT: Save actual results with real lat/lng coordinates
            businesses: resultsPayload.mapData.businesses, // Save businesses for reloading
            averageRank: data.averageRank,
            totalGridPoints: gridSize * gridSize,
            greenGridPoints: goodCount,
            yellowGridPoints: avgCount,
            redGridPoints: pins.filter(p => p.rank !== '20+' && typeof p.rank === 'number' && p.rank > 7).length,
            grayGridPoints: outOfTop20Count,
            analysisDate: new Date().toISOString(),
            profileId: profileId, // Include profile ID for proper scoping
            locationId: profileId // Also include as locationId for backward compatibility
          };
          
          logger.log('Saving heatmap snapshot with results and profile ID:', profileId, heatmapSnapshot);
          logger.log('Results array count:', data.results?.length);
          saveHeatmapSnapshot(heatmapSnapshot);
        }
      } catch (e) {
        logger.warn('Failed to fetch ranking:', e);
        
        // Fallback to mock data for demonstration
        const mockPins = [];
        const mockRanks = [14, 6, 18, 18, 18, 14, 6, '20+', 6, 6, 14, 14, '20+', 6, 6, 18, '20+', 6, 6, 6];
        
        for (let i = 0; i < gridSize * gridSize; i++) {
          const rank = mockRanks[i % mockRanks.length];
          mockPins.push({
            id: i,
            rank,
            color: rank === '20+' || rank > 7 ? '#EF232A' : rank <= 3 ? '#34A853' : '#FBBC05',
            position: { row: Math.floor(i / gridSize), col: i % gridSize }
          });
        }
        
        // Calculate mock stats
        const mockTotal = mockPins.length;
        const mockGoodCount = mockPins.filter(p => p.rank !== '20+' && typeof p.rank === 'number' && p.rank <= 3).length;
        const mockAvgCount = mockPins.filter(p => p.rank !== '20+' && typeof p.rank === 'number' && p.rank > 3 && p.rank <= 7).length;
        const mockPoorCount = mockPins.filter(p => p.rank === '20+' || (typeof p.rank === 'number' && p.rank > 7)).length;
        const mockOutOfTop20Count = mockPins.filter(p => p.rank === '20+').length;
        
        // Calculate average rank from mock pins
        const mockNumericRanks = mockPins.filter(p => typeof p.rank === 'number').map(p => p.rank);
        const mockAvgRank = mockNumericRanks.length > 0 
          ? mockNumericRanks.reduce((acc, rank) => acc + rank, 0) / mockNumericRanks.length 
          : null;
        
        const fallbackData = {
          businessName: businessName || 'Demo Business',
          keyword: keyword || 'Demo Keyword',
          date: new Date().toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
          stats: {
            good: Math.round((mockGoodCount / mockTotal) * 100),
            poor: Math.round((mockPoorCount / mockTotal) * 100),
            average: Math.round((mockAvgCount / mockTotal) * 100),
            averageRank: mockAvgRank ? Math.round(mockAvgRank * 10) / 10 : '—',
            shareOfVoice: Math.round((mockGoodCount / mockTotal) * 100),
            outOfTop20: Math.round((mockOutOfTop20Count / mockTotal) * 100)
          },
          mapData: {
            center: { lat: lat || 30.7333, lng: lng || 76.7794 },
            pins: mockPins,
            businesses: [
              { name: businessName || 'Your Business', rank: 6, address: '123 Demo Street', rating: 4.2, reviews: 45, isYou: true },
              { name: 'Competitor A', rank: 3, address: '456 Main St', rating: 4.5, reviews: 120 },
              { name: 'Competitor B', rank: 8, address: '789 Oak Ave', rating: 3.8, reviews: 67 }
            ],
            radius
          },
          history: [{
            gridIcon: true,
            gridSize: `${gridSize}*${gridSize}`,
            keyword: keyword || 'Demo Keyword',
            radius: `${radius} km`,
            listing: businessName || 'Demo Business',
            averageRank: 8.5,
            date: new Date().toLocaleDateString()
          }]
        };
        
        if (active) {
          setResultsData(fallbackData);
          setError(`Using demo data - ${e.message}`);
          
          // Save fallback heatmap results to storage for Performance page
          // Try to get locationId from URL params or match from localStorage profiles
          let profileId = locationId;
          if (!profileId) {
            try {
              const cachedProfiles = localStorage.getItem('availableGMBProfiles');
              if (cachedProfiles) {
                const profiles = JSON.parse(cachedProfiles);
                const matchedProfile = profiles.find(p => 
                  p.name && businessName && 
                  p.name.toLowerCase().trim() === businessName.toLowerCase().trim()
                );
                if (matchedProfile) {
                  profileId = matchedProfile.id || matchedProfile.locationId;
                  logger.log('[HeatmapResults] Matched profile ID from localStorage for fallback:', profileId);
                }
              }
            } catch (e) {
              logger.warn('[HeatmapResults] Could not match profile from localStorage for fallback:', e);
            }
          }
          
          const heatmapSnapshot = {
            businessName: fallbackData.businessName,
            keyword: fallbackData.keyword,
            gridSize,
            radius,
            centerLocation: fallbackData.mapData.center,
            stats: fallbackData.stats,
            pins: fallbackData.mapData.pins,
            businesses: fallbackData.mapData.businesses, // Save businesses for reloading
            averageRank: fallbackData.stats.averageRank,
            totalGridPoints: gridSize * gridSize,
            greenGridPoints: mockGoodCount,
            yellowGridPoints: mockAvgCount,
            redGridPoints: mockPins.filter(p => p.rank !== '20+' && typeof p.rank === 'number' && p.rank > 7).length,
            grayGridPoints: mockOutOfTop20Count,
            analysisDate: new Date().toISOString(),
            isDemoData: true,
            profileId: profileId, // Include profile ID for proper scoping
            locationId: profileId // Also include as locationId for backward compatibility
          };
          
          logger.log('Saving fallback heatmap snapshot with profile ID:', profileId, heatmapSnapshot);
          saveHeatmapSnapshot(heatmapSnapshot);
        }
      }
    }
    fetchRanking();
    return () => { active = false; };
  }, [businessName, keyword, gridSize, radius, lat, lng, isAdminView, analysisId, adminViewUserId]);

  const handleKeywordChange = (newKeyword) => {
    setCurrentKeyword(newKeyword);
    setResultsData(null); // Clear current data to show loading
    setError(null);
  };

  // Professional loading screen - Local SEO Map Tracker (White Background)
  if (!resultsData) {
    return (
      <DashboardLayout>
        <Box 
          sx={{ 
            minHeight: 'calc(100vh - 64px)', // Account for header
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: '#ffffff',
            overflow: 'hidden',
            position: 'relative'
          }}
        >
          {/* Animated background blobs */}
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              overflow: 'hidden',
              pointerEvents: 'none'
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                top: '25%',
                left: '25%',
                width: '256px',
                height: '256px',
                borderRadius: '50%',
                background: '#3b82f6',
                filter: 'blur(80px)',
                opacity: 0.08,
                animation: 'blobPulse 3s ease-in-out infinite'
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                bottom: '25%',
                right: '25%',
                width: '384px',
                height: '384px',
                borderRadius: '50%',
                background: '#a855f7',
                filter: 'blur(80px)',
                opacity: 0.08,
                animation: 'blobPulse 3s ease-in-out infinite',
                animationDelay: '1s'
              }}
            />
          </Box>

          {/* Main content */}
          <Box
            sx={{
              position: 'relative',
              zIndex: 10,
              textAlign: 'center',
              padding: '24px'
            }}
          >
            {/* Map pin icon with pulse animation */}
            <Box
              sx={{
                position: 'relative',
                display: 'inline-block',
                marginBottom: '32px'
              }}
            >
              {/* Pulse effect */}
              <LocationOn
                sx={{
                  position: 'absolute',
                  inset: 0,
                  width: { xs: '64px', sm: '80px' },
                  height: { xs: '64px', sm: '80px' },
                  color: '#3b82f6',
                  animation: 'ping 1.5s cubic-bezier(0, 0, 0.2, 1) infinite',
                  opacity: 0.2
                }}
              />
              {/* Main icon */}
              <LocationOn
                sx={{
                  width: { xs: '64px', sm: '80px' },
                  height: { xs: '64px', sm: '80px' },
                  color: '#3b82f6',
                  position: 'relative'
                }}
              />
            </Box>

            {/* Title */}
            <Typography
              variant="h3"
              sx={{
                fontSize: { xs: '1.5rem', sm: '2rem' },
                fontWeight: 700,
                color: '#1e293b',
                marginBottom: '12px',
                letterSpacing: '-0.025em'
              }}
            >
              Local SEO Map Tracker
            </Typography>

            {/* Subtitle */}
            <Typography
              sx={{
                color: '#64748b',
                marginBottom: '32px',
                fontSize: { xs: '0.875rem', sm: '1rem' }
              }}
            >
              Analyzing...
            </Typography>

            {/* Loading bar */}
            <Box
              sx={{
                width: '320px',
                maxWidth: '90vw',
                margin: '0 auto 24px'
              }}
            >
              <Box
                sx={{
                  height: '6px',
                  backgroundColor: '#e2e8f0',
                  borderRadius: '9999px',
                  overflow: 'hidden'
                }}
              >
                <Box
                  sx={{
                    height: '100%',
                    background: 'linear-gradient(to right, #3b82f6, #a855f7, #ec4899)',
                    borderRadius: '9999px',
                    animation: 'loadingBar 1.5s ease-in-out infinite'
                  }}
                />
              </Box>
            </Box>

            {/* Loading dots */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                gap: '8px'
              }}
            >
              <Box
                sx={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  backgroundColor: '#3b82f6',
                  animation: 'dotBounce 1.4s ease-in-out infinite',
                  animationDelay: '0s'
                }}
              />
              <Box
                sx={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  backgroundColor: '#a855f7',
                  animation: 'dotBounce 1.4s ease-in-out infinite',
                  animationDelay: '0.2s'
                }}
              />
              <Box
                sx={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  backgroundColor: '#ec4899',
                  animation: 'dotBounce 1.4s ease-in-out infinite',
                  animationDelay: '0.4s'
                }}
              />
            </Box>
          </Box>

          {/* CSS Animations */}
          <style>
            {`
              @keyframes blobPulse {
                0%, 100% {
                  opacity: 0.08;
                }
                50% {
                  opacity: 0.12;
                }
              }

              @keyframes ping {
                0% {
                  transform: scale(1);
                  opacity: 0.2;
                }
                75%, 100% {
                  transform: scale(1.5);
                  opacity: 0;
                }
              }

              @keyframes loadingBar {
                0% {
                  transform: translateX(-100%);
                }
                100% {
                  transform: translateX(400%);
                }
              }

              @keyframes dotBounce {
                0%, 80%, 100% {
                  transform: scale(0);
                  opacity: 0.5;
                }
                40% {
                  transform: scale(1);
                  opacity: 1;
                }
              }
            `}
          </style>
        </Box>
      </DashboardLayout>
    );
  }

  // Render results
  return (
    <DashboardLayout>
      <Stack
        spacing={{ xs: 2, sm: 2.5, md: 3 }}
        sx={{
          p: { xs: 1.5, sm: 2, md: 3 },
          maxWidth: '100%',
          overflow: 'hidden'
        }}
      >
        <HeatmapResultsHeader
          keyword={resultsData.keyword}
          date={resultsData.date}
          onKeywordChange={handleKeywordChange}
          resultsData={resultsData}
        />

        <HeatmapStatsSection stats={resultsData.stats} />

        <HeatmapMapSection
          mapData={resultsData.mapData}
          businessName={resultsData.businessName}
        />

        <HeatmapHistorySection history={resultsData.history} />
      </Stack>
    </DashboardLayout>
  );
};

export default HeatmapResultsPage;